import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {CONJUGATION_METHOD_OPTIONS} from 'molbio/adcs/AdcConjugationConstants';
import {adcConjugationSitesColumnCustomEval} from 'molbio/adcs/registration-table/adcConjugationSitesColumnCustomEval';
import {adcTargetDarColumnCustomEval} from 'molbio/adcs/registration-table/adcTargetDarColumnCustomEval';
import {SSI_ADC_INTERFACE_ID} from 'molbio/adcs/schema-configuration/AdcSchemaInterfaceConstants';
import {
  RegistrationTableRowData,
  SchemaInterfaceColumnCustomEval,
  SchemaInterfaceColumnTransformerBase,
} from 'notebook/integrations/registry/SchemaInterfaceColumnTransformerBase';
import {ImplementedStaticSchemaInterfaceGQL} from 'notebook/structured-tables/components/modals/RegistrationTableInsertModal';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import {
  AdcConjugationSitesStructuredTableColumn,
  CustomStructuredTableColumn,
  ExistingBioentityReferenceStructuredTableColumn,
  ExistingBioentityStructuredTableColumn,
  OptionSelectorStructuredTableColumn,
  StructuredTableColumn,
  StructuredTableColumnGroups,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {CustomEvalType} from 'notebook/tables/constants/CustomEvalConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';

export const ADC_PROTEIN_COLUMN_NAME = 'Protein';
export const ADC_LINKER_PAYLOAD_COLUMN_NAME = 'Linker-payload';
export const ADC_CONJUGATION_METHOD_COLUMN_NAME = 'Conjugation method';
export const ADC_CONJUGATION_SITES_COLUMN_NAME = 'Conjugation sites';
export const ADC_TARGET_DAR_COLUMN_NAME = 'Target DAR';

type AdcBioentityConfig = Pick<
  ExistingBioentityStructuredTableColumn,
  'bioentityTypeFilters' | 'schemaInterfaceId' | 'frontEndMetadata'
>;

// The picker config for the ADC entity-reference columns. Only `columnGroup` is persisted, so this must be
// re-injected on every load (see `configureAdcBioentityColumn`).

// Antibodies are custom entities (BASIC_FOLDER_ITEM), not the PROTEIN model type, so scope the picker by the
// antibody schema interface too.
const ADC_PROTEIN_PICKER_CONFIG: AdcBioentityConfig = {
  bioentityTypeFilters: [ModelTypeConstants.BASIC_FOLDER_ITEM],
  schemaInterfaceId: ANTIBODY_SCHEMA_INTERFACE_ID,
  frontEndMetadata: {validSchemas: 'ALL'},
};

const ADC_LINKER_PAYLOAD_PICKER_CONFIG: AdcBioentityConfig = {
  bioentityTypeFilters: [ModelTypeConstants.MOLECULE],
  frontEndMetadata: {validSchemas: 'ALL'},
};

function isAdcInterface(implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL): boolean {
  return implementedSchemaInterface.schemaInterface.id === SSI_ADC_INTERFACE_ID;
}

function makeAdcBioentityColumn(
  name: string,
  columnGroup: StructuredTableColumnGroups
): StructuredTableColumn {
  // EXISTING_BIOENTITY_REFERENCE is a dedicated columnType so shared registration/resync/primary-sample
  // handling skips these by type, preserving the operator's selection rather than overwriting it with the
  // registered ADC. They render like EXISTING_BIOENTITY columns; columnGroup distinguishes Protein
  // (antibody) from Linker-payload (molecule). Picker config is injected on load, not here.
  //
  // TODO(BNCH-171149 follow-up): the selections are not yet wired into ADC creation -- the submit parser
  // matches only the ungrouped primary EXISTING_BIOENTITY column, so these values do not yet drive
  // conjugate creation. Wiring them in must add server-side validation that the Protein is an antibody and
  // the Linker-payload is a molecule; today only the frontend picker enforces this.
  return makeStructuredTableColumn({
    columnType: TableColumnType.EXISTING_BIOENTITY_REFERENCE,
    name,
    columnGroup,
    isRequired: true,
    isReadOnly: false,
  });
}

function configureAdcBioentityColumn(
  column: StructuredTableColumn,
  config: AdcBioentityConfig
): ExistingBioentityReferenceStructuredTableColumn {
  assert(
    column.columnType === TableColumnType.EXISTING_BIOENTITY_REFERENCE,
    'ADC protein / linker-payload columns must be EXISTING_BIOENTITY_REFERENCE columns'
  );
  return {...column, ...config} as ExistingBioentityReferenceStructuredTableColumn;
}

function makeAdcConjugationMethodColumn(): OptionSelectorStructuredTableColumn {
  // Cells store the method's display name; read them with CONJUGATION_METHOD_DISPLAY_NAME_TO_ENUM.
  //
  // TODO(BNCH-171149 follow-up): like the Protein / Linker-payload columns, this value is not yet wired into
  // ADC creation.
  return {
    ...makeStructuredTableColumn({
      columnType: TableColumnType.OPTION_SELECTOR,
      name: ADC_CONJUGATION_METHOD_COLUMN_NAME,
      columnGroup: StructuredTableColumnGroups.ADC_CONJUGATION_METHOD,
      isRequired: true,
      isReadOnly: false,
    }),
    columnType: TableColumnType.OPTION_SELECTOR,
    options: CONJUGATION_METHOD_OPTIONS,
  };
}

function configureAdcConjugationMethodColumn(
  column: StructuredTableColumn
): OptionSelectorStructuredTableColumn {
  assert(
    column.columnType === TableColumnType.OPTION_SELECTOR,
    'ADC conjugation method column must be an OPTION_SELECTOR column'
  );
  return {...column, options: CONJUGATION_METHOD_OPTIONS} as OptionSelectorStructuredTableColumn;
}

function makeAdcConjugationSitesColumn(): AdcConjugationSitesStructuredTableColumn {
  // Cells store the selected sites as serialized JSON text (see serializeConjugationSites), matching the ADC
  // conjugation metadata's `conjugationSites: [ConjugationSite!]!`. Sites are chosen from a dropdown of the
  // row's candidate sites (see FormattedCellValue) and checked by adcConjugationSitesColumnCustomEval.
  //
  // TODO(BNCH-171149 follow-up): like the other ADC input columns, this value is not yet wired into ADC
  // creation.
  return {
    ...makeStructuredTableColumn({
      columnType: TableColumnType.ADC_CONJUGATION_SITES,
      name: ADC_CONJUGATION_SITES_COLUMN_NAME,
      columnGroup: StructuredTableColumnGroups.ADC_CONJUGATION_SITES,
      isRequired: false,
      isReadOnly: false,
    }),
    columnType: TableColumnType.ADC_CONJUGATION_SITES,
  };
}

function makeAdcTargetDarColumn(): CustomStructuredTableColumn {
  // An integer column, matching the ADC conjugation metadata's `targetDar: Int!`. The data type is persisted
  // in customColumnConfig, so unlike the other ADC columns there is nothing to re-inject on load. Bounds are
  // enforced by adcTargetDarColumnCustomEval (see ADC_COLUMN_SPECS).
  //
  // TODO(BNCH-171149 follow-up): like the other ADC input columns, this value is not yet wired into ADC
  // creation.
  return {
    ...makeStructuredTableColumn({
      columnType: TableColumnType.CUSTOM,
      name: ADC_TARGET_DAR_COLUMN_NAME,
      columnGroup: StructuredTableColumnGroups.ADC_TARGET_DAR,
      // TODO(BNCH-171153): require either Target DAR or selected conjugation sites, inferring DAR from the site
      // count when blank. Stochastic methods show every candidate site, so DAR stays required for them.
      isRequired: true,
      isReadOnly: false,
    }),
    columnType: TableColumnType.CUSTOM,
    customColumnConfig: {dataType: CustomColumnDataType.INTEGER},
  };
}

/**
 * Everything the transformer contributes for one ADC column, so that each column is defined in one place.
 * getNewColumns, configureColumn and getCustomEval each read from ADC_COLUMN_SPECS.
 */
interface AdcColumnSpec {
  columnGroup: StructuredTableColumnGroups;
  /** Builds the column for a new registration table. */
  makeColumn: () => StructuredTableColumn;
  /** Re-injects config that is not persisted onto a loaded column. Runs on every load. */
  configure?: (column: StructuredTableColumn) => StructuredTableColumn;
  /** The custom eval that validates the column's cells. Looked up on every evaluation. */
  customEval?: SchemaInterfaceColumnCustomEval;
}

/** The ADC columns, in the order they are added to a registration table. */
const ADC_COLUMN_SPECS: ReadonlyArray<AdcColumnSpec> = [
  {
    columnGroup: StructuredTableColumnGroups.ADC_PROTEIN,
    makeColumn: () =>
      makeAdcBioentityColumn(ADC_PROTEIN_COLUMN_NAME, StructuredTableColumnGroups.ADC_PROTEIN),
    configure: (column) => configureAdcBioentityColumn(column, ADC_PROTEIN_PICKER_CONFIG),
  },
  {
    columnGroup: StructuredTableColumnGroups.ADC_LINKER_PAYLOAD,
    makeColumn: () =>
      makeAdcBioentityColumn(ADC_LINKER_PAYLOAD_COLUMN_NAME, StructuredTableColumnGroups.ADC_LINKER_PAYLOAD),
    configure: (column) => configureAdcBioentityColumn(column, ADC_LINKER_PAYLOAD_PICKER_CONFIG),
  },
  {
    columnGroup: StructuredTableColumnGroups.ADC_CONJUGATION_METHOD,
    makeColumn: makeAdcConjugationMethodColumn,
    configure: configureAdcConjugationMethodColumn,
  },
  {
    columnGroup: StructuredTableColumnGroups.ADC_CONJUGATION_SITES,
    makeColumn: makeAdcConjugationSitesColumn,
    // Validated against the row's protein and conjugation method, in the order
    // adcConjugationSitesColumnCustomEval expects them.
    customEval: {
      customEvalFn: adcConjugationSitesColumnCustomEval,
      customEvalType: CustomEvalType.DEFAULT,
      dependencyColumnGroups: [
        StructuredTableColumnGroups.ADC_PROTEIN,
        StructuredTableColumnGroups.ADC_CONJUGATION_METHOD,
      ],
    },
  },
  {
    columnGroup: StructuredTableColumnGroups.ADC_TARGET_DAR,
    makeColumn: makeAdcTargetDarColumn,
    customEval: {customEvalFn: adcTargetDarColumnCustomEval, customEvalType: CustomEvalType.DEFAULT},
  },
];

const ADC_COLUMN_SPECS_BY_GROUP: ReadonlyMap<StructuredTableColumn['columnGroup'], AdcColumnSpec> = new Map(
  ADC_COLUMN_SPECS.map((spec) => [spec.columnGroup, spec])
);

/**
 * Adds the ADC conjugate's input columns to a registration table for schemas implementing `ssi_adc`,
 * mirroring AntibodyRegTableColumnTransformer: a required Protein column scoped to antibodies, a
 * required Linker-payload column scoped to molecules, a required Conjugation method column, an optional
 * Conjugation sites column chosen from the row's protein and method, and a required integer Target DAR column.
 */
export default class AdcRegTableColumnTransformer implements SchemaInterfaceColumnTransformerBase {
  replaceExistingColumn(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    tableRowData: RegistrationTableRowData
  ): StructuredTableColumn | null {
    return null;
  }

  getNewColumns(
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL,
    tableRowData: RegistrationTableRowData
  ): Array<StructuredTableColumn> {
    assert(isAdcInterface(implementedSchemaInterface), 'ADC transformer called with non-ADC interface');
    // Added regardless of whether the ADC is new or already exists. ADCs have no computed fields
    // surfacing these inputs, so (unlike antibodies) there is nothing for shouldHideColumn to hide.
    return ADC_COLUMN_SPECS.map((spec) => spec.makeColumn());
  }

  shouldHideColumn(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): boolean {
    return false;
  }

  getFrontEndMetadata(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): unknown | null {
    return null;
  }

  /**
   * Re-injects the picker config or option list onto an ADC column, keyed on its column group; returns null
   * for non-ADC columns and ADC columns with nothing to re-inject. Runs on every load (including
   * rehydration) since the config is not persisted. Implemented (rather than getFrontEndMetadata) because ADC
   * columns need top-level fields set, not just a frontEndMetadata blob.
   */
  configureColumn(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): StructuredTableColumn | null {
    return ADC_COLUMN_SPECS_BY_GROUP.get(column.columnGroup)?.configure?.(column) ?? null;
  }

  /** Supplies the custom eval for ADC columns that validate their cells, keyed on column group. */
  getCustomEval(
    column: StructuredTableColumn,
    implementedSchemaInterface: ImplementedStaticSchemaInterfaceGQL
  ): SchemaInterfaceColumnCustomEval | null {
    return ADC_COLUMN_SPECS_BY_GROUP.get(column.columnGroup)?.customEval ?? null;
  }
}
